import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Gift,
  ChevronRight,
  ChevronLeft,
  Check,
  Sparkles,
  Wallet,
  ShieldCheck,
  BadgeCheck,
  CircleAlert,
  Info,
  TrendingUp,
  Copy,
} from "lucide-react";

import { useAuth } from "../../contexts/AuthContext";
import { api, formatApiErrorDetail } from "../../lib/api";
import { copyToClipboard } from "../../lib/clipboard";
import { ProfileShell } from "../../components/profile/ProfileShell";
import { PaymentIcon } from "../../components/profile/PaymentIcons";
import SelectPromotionSheet from "../../components/profile/SelectPromotionSheet";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../../components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";

const METHODS = [
  { id: "bkash", name: "bKash", category: "ewallet", types: ["bKash Payment", "bKash P2C"] },
  { id: "nagad", name: "Nagad", category: "ewallet", types: ["Nagad Payment", "Nagad P2C"] },
  { id: "rocket", name: "Rocket", category: "ewallet", types: ["Rocket Payment"] },
  { id: "upay", name: "UPay", category: "ewallet", types: ["UPay Payment"] },
  { id: "usdt_trc20", name: "USDT TRC-20", category: "crypto", types: ["USDT (TRC-20)"] },
  { id: "usdt_bep20", name: "USDT BEP-20", category: "crypto", types: ["USDT (BEP-20)"] },
  { id: "trx", name: "TRX", category: "crypto", types: ["TRX (TRC-20)"] },
  { id: "bnb", name: "BNB", category: "crypto", types: ["BNB (BEP-20)"] },
  { id: "eth", name: "ETH", category: "crypto", types: ["ETH (ERC-20)"] },
  { id: "ton", name: "TON", category: "crypto", types: ["TON"] },
  { id: "usdc_polygon", name: "USDC Polygon", category: "crypto", types: ["USDC (Polygon)"] },
];

const CATEGORIES = [
  { id: "ewallet", label: "E-wallet" },
  { id: "crypto", label: "Crypto" },
];

const FALLBACK_PROMOTIONS = [
  { id: "normal", name: "Normal", description: "No bonus", bonus_pct: 0 },
];

const QUICK = [500, 1000, 2000, 5000, 10000, 20000];

export default function DepositPage() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();

  const [promotions, setPromotions] = useState(FALLBACK_PROMOTIONS);
  const [depositCfg, setDepositCfg] = useState({ enabled: true, notice: "Funds credit automatically after the gateway confirms." });
  const [enabledCoins, setEnabledCoins] = useState(null); // null = not loaded; {id: true} when ready
  const [promotionId, setPromotionId] = useState("normal");
  const [selectedPromo, setSelectedPromo] = useState(null);
  const [step, setStep] = useState("home"); // "home" | "form"
  const [categoryId, setCategoryId] = useState("ewallet");
  const [methodId, setMethodId] = useState("bkash");
  const [typeValue, setTypeValue] = useState("bKash Payment");
  const [gateways, setGateways] = useState([]);
  const [gatewayId, setGatewayId] = useState("");
  const [loadingGateways, setLoadingGateways] = useState(false);
  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [promoOpen, setPromoOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const activeMethod = useMemo(
    () => METHODS.find((m) => m.id === methodId),
    [methodId]
  );
  const activePromo = useMemo(
    () => selectedPromo || { id: "normal", name: "Normal", bonus_pct: 0 },
    [selectedPromo]
  );
  const activeGateway = useMemo(
    () => gateways.find((g) => g.id === gatewayId) || null,
    [gateways, gatewayId]
  );

  useEffect(() => {
    if (activeMethod?.types?.length) setTypeValue(activeMethod.types[0]);
  }, [activeMethod]);

  useEffect(() => {
    let cancelled = false;
    // Load promotions + deposit-config from admin
    api
      .get("/promotions")
      .then((r) => {
        if (cancelled) return;
        const items = r.data?.items || [];
        if (items.length) {
          setPromotions(items);
        }
      })
      .catch(() => {});
    api
      .get("/deposit-config")
      .then((r) => {
        if (cancelled) return;
        setDepositCfg({
          enabled: r.data?.enabled !== false,
          notice: r.data?.notice || "Funds credit automatically after the gateway confirms.",
        });
        // Build enabled-coins map from admin config
        const coins = r.data?.crypto?.coins || {};
        if (Object.keys(coins).length > 0) {
          const map = {};
          Object.entries(coins).forEach(([k, v]) => { map[k] = !!v?.enabled; });
          setEnabledCoins(map);
        } else {
          // fallback — enable all known crypto methods
          const map = {};
          METHODS.filter((m) => m.category === "crypto").forEach((m) => { map[m.id] = true; });
          setEnabledCoins(map);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoadingGateways(true);
    api
      .get(`/payments/gateways?method=${methodId}`)
      .then((r) => {
        if (cancelled) return;
        const items = r.data?.items || [];
        setGateways(items);
        const preferred = items.find((g) => g.recommended) || items[0];
        setGatewayId(preferred?.id || "");
      })
      .catch(() => {
        if (!cancelled) {
          setGateways([]);
          setGatewayId("");
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingGateways(false);
      });
    return () => {
      cancelled = true;
    };
  }, [methodId]);

  const minAmt = activeGateway?.min_amount || 500;
  const maxAmt = activeGateway?.max_amount || 0;
  const amt = Number(amount) || 0;

  // Bonus preview
  const bonusAmt = useMemo(() => {
    const pct = activePromo?.bonus_pct ?? activePromo?.bonusPct ?? 0;
    if (!pct) return 0;
    const raw = (amt * pct) / 100;
    const cap = activePromo?.cap || 0;
    return cap ? Math.min(raw, cap) : raw;
  }, [amt, activePromo]);
  const totalCredit = amt + bonusAmt;

  const canSubmit =
    depositCfg.enabled &&
    !!methodId &&
    !!typeValue &&
    !!gatewayId &&
    amt >= minAmt &&
    (maxAmt === 0 || amt <= maxAmt);

  const copyBalance = async () => {
    const ok = await copyToClipboard(String(user?.balance ?? 0));
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!gatewayId) return toast.error("Please select a deposit channel");
    if (amt < minAmt)
      return toast.error(
        `Minimum for ${activeGateway?.label} is ৳${minAmt.toLocaleString()}`
      );
    if (maxAmt && amt > maxAmt)
      return toast.error(
        `Maximum for ${activeGateway?.label} is ৳${maxAmt.toLocaleString()}`
      );
    // Client-side bonus min-deposit guard (server enforces too)
    const promoMin = Number(activePromo?.min_deposit || 0);
    if (promoMin && (activePromo?.bonus_pct || 0) > 0 && amt < promoMin) {
      return toast.error(
        `This bonus requires a minimum deposit of ৳${promoMin.toLocaleString()}`
      );
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/profile/wallet/deposit", {
        amount: amt,
        method: `${activeMethod.name} · ${typeValue} · ${activeGateway?.label}`,
        gateway_method: activeMethod.id,
        gateway_id: gatewayId,
        promotion_id: promotionId && promotionId !== "normal" ? promotionId : null,
      });
      if (data?.redirect_url) {
        const depId = data.deposit_id;
        const url = String(data.redirect_url);
        const isInternal = url.startsWith("/");
        if (isInternal) {
          // Crypto / in-site cashier — navigate inside the SPA.
          navigate(url);
          return;
        }
        // External gateway: open in a new tab and keep polling here.
        const checkoutWin = window.open(url, "_blank", "noopener,noreferrer");
        toast.info(checkoutWin ? "Checkout opened in a new tab" : "Redirecting to secure checkout…", {
          description: checkoutWin
            ? "Complete the payment, then return here. Your balance will update automatically."
            : "If the checkout doesn't open, please allow popups for this site.",
        });
        navigate(`/profile/deposit/return?ref=${depId}&checkout=${encodeURIComponent(url)}`);
        return;
      }
      await refreshUser();
      toast.success("Deposit submitted", {
        description: `${data?.gateway?.label || ""} · Ref ${
          data?.reference || data?.deposit_id?.slice(0, 8)
        } · ৳${amt.toLocaleString()}`,
        icon: <Check className="h-4 w-4 text-emerald-400" />,
      });
      setAmount("");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || "Deposit failed"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const sectionLabel = "text-[11px] uppercase tracking-[0.2em] text-white/50 font-semibold mb-2.5";
  const fieldBase =
    "rounded-2xl h-14 px-4 bg-[#0f1723] border border-white/10 text-white text-base placeholder:text-white/35 focus-visible:ring-emerald-500/60 focus-visible:ring-2 focus-visible:border-emerald-500/60 transition";

  const pageTitle = step === "home" ? "Deposit" : (categoryId === "crypto" ? "Crypto" : "E-wallet");

  // ---- Home view (category cards) ----
  if (step === "home") {
    const ewalletCoins = METHODS.filter((m) => m.category === "ewallet").slice(0, 3);
    const cryptoCoins = [
      { id: "usdt", logo: "/payment-logos/usdt.svg", bg: "#26a17b" },
      { id: "btc",  logo: "/payment-logos/btc.svg",  bg: "#f7931a" },
      { id: "eth",  logo: "/payment-logos/eth.svg",  bg: "#627eea" },
    ];
    const ewalletBrands = {
      bkash:  { logo: "/payment-logos/bkash.png",       bg: "#ffffff" },
      nagad:  { logo: "/payment-logos/nagad.png",       bg: "#ffffff" },
      rocket: { logo: "/payment-logos/rocket.png",      bg: "#ffffff" },
      upay:   { logo: "/payment-logos/upay.png",        bg: "#ffffff" },
    };
    const openCategory = (catId) => {
      setCategoryId(catId);
      const first = METHODS.find((m) => m.category === catId);
      if (first) setMethodId(first.id);
      setStep("form");
    };
    return (
      <ProfileShell title="Deposit">
        <div className="max-w-md mx-auto pb-32 lg:pb-10 pt-2 space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-500" data-testid="deposit-home">
          {!depositCfg.enabled && (
            <div data-testid="deposit-disabled-banner" className="rounded-2xl px-4 py-3 flex items-start gap-2.5 bg-rose-500/10 border border-rose-500/30 text-rose-200 text-sm">
              <CircleAlert className="h-4 w-4 mt-0.5 shrink-0" />
              Deposits are temporarily disabled. Please check back later.
            </div>
          )}

          {/* Balance pill */}
          <div className="flex items-center justify-between rounded-2xl px-4 py-3 bg-[#0f1723] border border-white/10" data-testid="deposit-balance-pill">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/50 font-bold">Current balance</div>
              <div className="flex items-baseline gap-1.5 mt-1">
                <span className="text-[11px] text-white/45 font-semibold">৳</span>
                <span className="text-2xl font-extrabold text-white tabular-nums">{Number(user?.balance || 0).toLocaleString("en-IN")}</span>
              </div>
            </div>
            <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-emerald-500/15 border border-emerald-400/25">
              <Wallet className="h-5 w-5 text-emerald-400" />
            </div>
          </div>

          {/* Category cards (E-wallet + Crypto) */}
          <button
            type="button"
            data-testid="deposit-card-ewallet"
            onClick={() => openCategory("ewallet")}
            disabled={!depositCfg.enabled}
            className="group w-full rounded-2xl px-3 sm:px-5 py-4 sm:py-7 bg-[#16202d] hover:bg-[#1a2635] border border-white/[0.06] hover:border-emerald-500/40 transition-all text-left flex items-center gap-2 sm:gap-3 disabled:opacity-50"
          >
            <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-xl bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 border border-emerald-500/30 grid place-items-center shrink-0">
              <Wallet className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-300" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-white text-base sm:text-lg font-extrabold leading-tight whitespace-nowrap">E-wallet</div>
              <div className="sm:hidden text-emerald-300 text-[10px] font-bold mt-0.5">Up to 100%</div>
            </div>
            <div className="flex -space-x-1.5 sm:-space-x-2 shrink-0">
              {ewalletCoins.map((m, i) => {
                const b = ewalletBrands[m.id];
                if (!b) return null;
                return (
                  <div
                    key={m.id}
                    className="h-7 w-7 sm:h-10 sm:w-10 rounded-lg sm:rounded-xl ring-2 ring-[#16202d] flex items-center justify-center overflow-hidden shadow-lg"
                    style={{ background: b.bg, zIndex: 10 - i, padding: m.id === "nagad" || m.id === "upay" ? "3px" : "4px" }}
                    data-testid={`ewallet-preview-${m.id}`}
                    title={m.name}
                  >
                    <img src={b.logo} alt={m.name} className="h-full w-full object-contain" draggable={false} />
                  </div>
                );
              })}
            </div>
            <span className="hidden sm:inline text-emerald-300 text-[12px] font-bold whitespace-nowrap">Up to 100%</span>
            <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-300/70 shrink-0" />
          </button>

          <button
            type="button"
            data-testid="deposit-card-crypto"
            onClick={() => openCategory("crypto")}
            disabled={!depositCfg.enabled}
            className="group w-full rounded-2xl px-3 sm:px-5 py-4 sm:py-7 bg-[#16202d] hover:bg-[#1a2635] border border-white/[0.06] hover:border-amber-400/40 transition-all text-left flex items-center gap-2 sm:gap-3 disabled:opacity-50"
          >
            <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-xl bg-gradient-to-br from-amber-500/20 to-amber-500/5 border border-amber-500/30 grid place-items-center shrink-0">
              <CryptoRingIcon />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-white text-base sm:text-lg font-extrabold leading-tight whitespace-nowrap">Crypto</div>
              <div className="sm:hidden text-amber-300 text-[10px] font-bold mt-0.5">Up to 100%</div>
            </div>
            <div className="flex -space-x-1.5 sm:-space-x-2 shrink-0">
              {cryptoCoins.map((c, i) => (
                <div
                  key={c.id}
                  className="h-7 w-7 sm:h-10 sm:w-10 rounded-lg sm:rounded-xl ring-2 ring-[#16202d] overflow-hidden shadow-lg flex items-center justify-center"
                  style={{ background: c.bg, zIndex: 10 - i }}
                  data-testid={`crypto-preview-${c.id}`}
                >
                  <img src={c.logo} alt={c.id} className="h-full w-full object-contain" draggable={false} />
                </div>
              ))}
            </div>
            <span className="hidden sm:inline text-emerald-300 text-[12px] font-bold whitespace-nowrap">Up to 100%</span>
            <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 text-amber-300/70 shrink-0" />
          </button>

          <div className="rounded-2xl px-4 py-3 flex items-start gap-2.5 bg-white/[0.03] border border-white/10 text-xs text-white/55 leading-relaxed">
            <ShieldCheck className="h-4 w-4 mt-0.5 text-emerald-400/80 shrink-0" />
            Auto payment · confirmed in seconds · 24/7 secure
          </div>
        </div>
      </ProfileShell>
    );
  }

  return (
    <ProfileShell title={pageTitle}>
      <div className="max-w-md mx-auto pb-32 lg:pb-10">
        <form
          onSubmit={submit}
          className="pt-2 space-y-7 animate-in fade-in slide-in-from-bottom-2 duration-500"
          data-testid="deposit-form"
        >
          {/* Back to category picker */}
          <button
            type="button"
            data-testid="deposit-back-button"
            onClick={() => setStep("home")}
            className="inline-flex items-center gap-1.5 text-white/60 hover:text-emerald-300 text-[12px] font-semibold -mt-1 -mb-3"
          >
            <ChevronLeft className="h-4 w-4" /> Back to methods
          </button>
          {!depositCfg.enabled && (
            <div
              data-testid="deposit-disabled-banner"
              className="rounded-2xl px-4 py-3 flex items-start gap-2.5 bg-rose-500/10 border border-rose-500/30 text-rose-200 text-sm"
            >
              <CircleAlert className="h-4 w-4 mt-0.5 shrink-0" />
              Deposits are temporarily disabled. Please check back later.
            </div>
          )}
          {/* Balance card */}
          <section
            className="relative overflow-hidden rounded-3xl p-4 sm:p-5"
            data-testid="balance-card"
            style={{
              background:
                "radial-gradient(140% 100% at 10% 0%, rgba(28,196,110,0.28) 0%, rgba(10,17,25,0) 55%), linear-gradient(180deg,#0e1a26 0%,#0a1119 100%)",
              border: "1px solid rgba(28,196,110,0.22)",
              boxShadow:
                "0 20px 60px -30px rgba(28,196,110,0.6), inset 0 1px 0 rgba(255,255,255,0.04)",
            }}
          >
            {/* decorative dots */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 opacity-[0.08]"
              style={{
                backgroundImage:
                  "radial-gradient(rgba(255,255,255,0.8) 1px, transparent 1px)",
                backgroundSize: "14px 14px",
              }}
            />
            <div className="relative flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-1.5 text-white/55 text-[10px] uppercase tracking-[0.24em] font-bold">
                  Current balance
                  <BadgeCheck className="h-3.5 w-3.5 text-emerald-400" />
                </div>
                <div className="flex items-end gap-1.5 mt-1.5 leading-none">
                  <span className="text-base sm:text-lg text-emerald-300/80 font-semibold pb-1">
                    ৳
                  </span>
                  <span className="text-[26px] sm:text-[38px] leading-none font-display font-extrabold tabular-nums text-white tracking-tight">
                    {Number(user?.balance || 0).toLocaleString("en-IN")}
                  </span>
                  <button
                    type="button"
                    onClick={copyBalance}
                    className="text-white/35 hover:text-emerald-300 transition ml-1"
                    aria-label="Copy balance"
                  >
                    {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  </button>
                </div>
                <div className="mt-2.5 flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold text-emerald-300 bg-emerald-500/10 border border-emerald-400/30">
                    <TrendingUp className="h-3 w-3" /> {user?.vip_tier || "Normal"}
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium text-white/60 bg-white/[0.04] border border-white/10">
                    {user?.vip_points || 0} VIP pts
                  </span>
                </div>
              </div>
              <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-2xl flex items-center justify-center bg-emerald-500/15 border border-emerald-400/25 shrink-0">
                <Wallet className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-400" />
              </div>
            </div>
            <div className="relative mt-4 flex items-center gap-2 text-[11px] text-emerald-300/85">
              <ShieldCheck className="h-3.5 w-3.5 shrink-0" />
              Auto payment · confirmed in seconds · 24/7 secure
            </div>
          </section>

          {/* Promotion */}
          <section>
            <div className={sectionLabel}>Select promotion</div>
            <button
              type="button"
              data-testid="promotion-trigger"
              onClick={() => setPromoOpen(true)}
              className="w-full rounded-2xl px-4 h-14 flex items-center justify-between border border-white/10 bg-[#0f1723] hover:border-emerald-400/30 hover:bg-[#11202d] transition group"
            >
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-lg flex items-center justify-center bg-emerald-500/10 border border-emerald-400/20">
                  <Gift className="h-4 w-4 text-emerald-400" />
                </div>
                <div className="text-left">
                  <div className="font-semibold text-white leading-tight">
                    {activePromo?.name || "Normal"}
                  </div>
                  {!!(activePromo?.bonus_pct ?? activePromo?.bonusPct) && (
                    <div className="text-[11px] text-emerald-300/85">
                      +{activePromo.bonus_pct ?? activePromo.bonusPct}% bonus
                    </div>
                  )}
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-white/40 group-hover:text-emerald-300 transition" />
            </button>
            <SelectPromotionSheet
              open={promoOpen}
              selectedId={selectedPromo ? promotionId : "__none__"}
              onClose={() => setPromoOpen(false)}
              onConfirm={(p) => {
                if (p) {
                  setSelectedPromo(p);
                  setPromotionId(p.id);
                } else {
                  setSelectedPromo(null);
                  setPromotionId("normal");
                }
                setPromoOpen(false);
              }}
            />
          </section>

          {/* Payment methods */}
          <section>
            <div className={sectionLabel}>Select payment</div>
            <div className="grid grid-cols-2 gap-2 mb-3" data-testid="deposit-category-tabs">
              {CATEGORIES.map((c) => {
                const active = categoryId === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    data-testid={`deposit-category-${c.id}`}
                    onClick={() => {
                      setCategoryId(c.id);
                      const first = METHODS.find((m) => m.category === c.id);
                      if (first) setMethodId(first.id);
                    }}
                    className={`rounded-2xl h-11 px-4 text-sm font-semibold border transition ${
                      active
                        ? "border-emerald-500 bg-emerald-500/10 text-emerald-200"
                        : "border-white/10 bg-[#0f1723] text-white/70 hover:border-emerald-500/40"
                    }`}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {METHODS.filter((m) => m.category === categoryId && (m.category !== "crypto" || !enabledCoins || enabledCoins[m.id])).map((m) => {
                const active = methodId === m.id;
                return (
                  <button
                    key={m.id}
                    type="button"
                    data-testid={`payment-method-${m.id}`}
                    onClick={() => setMethodId(m.id)}
                    className={`relative rounded-xl sm:rounded-2xl p-2 sm:p-3.5 flex flex-col items-center justify-center gap-1.5 sm:gap-2.5 h-[88px] sm:h-[124px] border transition-all overflow-hidden ${
                      active
                        ? "border-emerald-500 bg-emerald-500/5"
                        : "border-white/10 bg-[#0f1723] hover:-translate-y-[2px] hover:border-emerald-500/40"
                    }`}
                    style={
                      active
                        ? {
                            boxShadow:
                              "0 12px 30px -12px rgba(28,196,110,0.55), inset 0 1px 0 rgba(255,255,255,0.05)",
                          }
                        : undefined
                    }
                  >
                    {active && (
                      <span className="absolute top-1.5 right-1.5 sm:top-2 sm:right-2 h-3.5 w-3.5 sm:h-4 sm:w-4 rounded-full bg-emerald-500 flex items-center justify-center text-white">
                        <Check className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                      </span>
                    )}
                    <PaymentIcon id={m.id} />
                    <span className="text-[12px] sm:text-sm font-semibold text-white">
                      {m.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>

          {/* Type */}
          <section>
            <div className={sectionLabel}>Select type</div>
            <RadioGroup
              value={typeValue}
              onValueChange={setTypeValue}
              className="space-y-2"
            >
              {(activeMethod?.types || []).map((t) => {
                const active = typeValue === t;
                return (
                  <label
                    key={t}
                    data-testid={`type-option-${t
                      .toLowerCase()
                      .replace(/\s+/g, "-")}`}
                    className={`flex items-center justify-between rounded-xl sm:rounded-2xl px-3 sm:px-4 h-12 sm:h-14 cursor-pointer transition border ${
                      active
                        ? "border-emerald-500 bg-emerald-500/[0.06]"
                        : "border-white/10 bg-[#0f1723] hover:border-emerald-400/30"
                    }`}
                  >
                    <span className="text-white font-medium text-sm sm:text-base">{t}</span>
                    <RadioGroupItem
                      value={t}
                      className="h-5 w-5 border-white/30 data-[state=checked]:border-emerald-500 data-[state=checked]:bg-emerald-500"
                    />
                  </label>
                );
              })}
            </RadioGroup>
          </section>

          {/* Channel */}
          <section>
            <div className={`${sectionLabel} flex items-center justify-between`}>
              <span>Deposit channel</span>
              {gateways.length > 1 && (
                <span className="text-white/40 normal-case tracking-normal text-[11px] font-medium">
                  {gateways.length} available
                </span>
              )}
            </div>
            {loadingGateways ? (
              <div className="rounded-2xl h-14 px-4 flex items-center text-white/50 bg-[#0f1723] border border-white/10">
                Loading channels…
              </div>
            ) : gateways.length === 0 ? (
              <div
                data-testid="no-channels"
                className="rounded-2xl px-4 py-3 bg-rose-500/5 border border-rose-500/30 text-rose-300 text-sm flex items-start gap-2"
              >
                <CircleAlert className="h-4 w-4 mt-0.5 shrink-0" />
                No active channel for {activeMethod?.name}. Please choose another payment method.
              </div>
            ) : (
              <Select value={gatewayId} onValueChange={setGatewayId}>
                <SelectTrigger
                  data-testid="channel-select"
                  className="w-full rounded-2xl h-14 px-4 text-left text-white bg-[#0f1723] border border-white/10 focus:border-emerald-500/60 focus:ring-emerald-500/40"
                >
                  <SelectValue>
                    <span className="flex items-center justify-between w-full">
                      <span className="font-semibold">
                        {activeGateway?.label || "Select channel"}
                      </span>
                      {activeGateway?.recommended && (
                        <Badge
                          variant="outline"
                          className="ml-3 border-emerald-500/40 text-emerald-300 bg-emerald-500/10 text-[10px] px-2 py-0 font-semibold"
                        >
                          Recommend
                        </Badge>
                      )}
                    </span>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="bg-[#0f1723] border-white/10 text-white">
                  {gateways.map((g) => (
                    <SelectItem
                      key={g.id}
                      value={g.id}
                      data-testid={`channel-option-${g.id}`}
                    >
                      <div className="flex items-center justify-between w-full gap-4">
                        <div className="flex flex-col">
                          <span className="font-semibold">{g.label}</span>
                          <span className="text-[11px] text-white/45">
                            ৳{(g.min_amount || 0).toLocaleString("en-IN")} – {" "}
                            {g.max_amount
                              ? `৳${g.max_amount.toLocaleString("en-IN")}`
                              : "∞"}
                          </span>
                        </div>
                        {g.recommended && (
                          <span className="text-xs text-emerald-400 shrink-0">
                            Recommend
                          </span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </section>

          {/* Amount */}
          <section>
            <div className={`${sectionLabel} flex items-center justify-between`}>
              <span>Deposit amount (BDT)</span>
              <span className="text-[10px] text-white/40 font-medium normal-case tracking-normal">
                Min ৳{minAmt.toLocaleString("en-IN")}
                {maxAmt ? ` · Max ৳${maxAmt.toLocaleString("en-IN")}` : ""}
              </span>
            </div>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl font-display font-extrabold text-white/40 pointer-events-none">
                ৳
              </span>
              <Input
                data-testid="deposit-amount"
                inputMode="numeric"
                placeholder={`${minAmt.toLocaleString("en-IN")}`}
                value={amount}
                onChange={(e) =>
                  setAmount(e.target.value.replace(/[^0-9.]/g, ""))
                }
                className={`${fieldBase} pl-10 text-xl font-display font-extrabold tabular-nums h-16`}
              />
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2.5">
              {QUICK.map((q) => {
                const active = Number(amount) === q;
                const disabled =
                  q < minAmt || (maxAmt > 0 && q > maxAmt);
                return (
                  <button
                    key={q}
                    type="button"
                    data-testid={`quick-amount-${q}`}
                    disabled={disabled}
                    onClick={() => setAmount(String(q))}
                    className={`h-11 rounded-xl text-sm font-bold border transition ${
                      disabled
                        ? "border-white/5 bg-white/[0.02] text-white/20 cursor-not-allowed"
                        : active
                        ? "border-emerald-500 bg-emerald-500/10 text-emerald-300 shadow-[0_6px_20px_-10px_rgba(28,196,110,0.6)]"
                        : "border-white/10 bg-[#0f1723] text-white/80 hover:border-emerald-500/40 hover:text-emerald-200"
                    }`}
                  >
                    ৳{q.toLocaleString("en-IN")}
                  </button>
                );
              })}
            </div>
          </section>

          {/* Info note */}
          <div className="rounded-2xl px-4 py-3 flex items-start gap-2.5 bg-white/[0.03] border border-white/10 text-xs text-white/55 leading-relaxed">
            <Info className="h-4 w-4 mt-0.5 text-white/45 shrink-0" />
            <span data-testid="deposit-notice">
              After submitting, complete the payment in your wallet app. {depositCfg.notice}
            </span>
          </div>

          {/* ============ INLINE CTA (desktop) ============ */}
          <div className="hidden lg:block">
            {amt >= minAmt && bonusAmt > 0 && (
              <div className="mb-2.5 flex items-center justify-between text-xs px-1">
                <span className="text-white/55">Bonus</span>
                <span className="font-semibold text-emerald-300">
                  +৳{bonusAmt.toLocaleString("en-IN")} (total ৳
                  {totalCredit.toLocaleString("en-IN")})
                </span>
              </div>
            )}
            <button
              type="button"
              data-testid="deposit-submit-desktop"
              onClick={submit}
              disabled={!canSubmit || submitting}
              className="w-full h-14 rounded-2xl text-white font-display font-extrabold tracking-wide active:scale-[0.99] transition disabled:opacity-60 disabled:cursor-not-allowed"
              style={{
                background:
                  "linear-gradient(180deg,#21c46e 0%,#19a55c 55%,#12904e 100%)",
                boxShadow:
                  "0 10px 28px -10px rgba(25,165,92,0.65), inset 0 1px 0 rgba(255,255,255,0.25)",
              }}
            >
              {submitting
                ? "Processing…"
                : amt >= minAmt
                ? `Continue · Deposit ৳${amt.toLocaleString("en-IN")}`
                : "Continue"}
            </button>
            <p className="mt-2 text-[11px] text-white/40 text-center">
              Funds credit automatically after the gateway confirms.
            </p>
          </div>
        </form>
      </div>

      {/* ============ MOBILE STICKY CTA ============ */}
      <div
        className="fixed inset-x-0 z-40 lg:hidden"
        style={{ bottom: "calc(env(safe-area-inset-bottom) + 64px)" }}
      >
        <div
          aria-hidden
          className="absolute inset-x-0 -top-8 h-8 bg-gradient-to-t from-[#0a1119] to-transparent pointer-events-none"
        />
        <div className="relative px-4 py-3 bg-[#0a1119]/95 backdrop-blur border-t border-white/5">
          <div className="max-w-2xl mx-auto">
            {amt >= minAmt && bonusAmt > 0 && (
              <div className="mb-2 flex items-center justify-between text-[11px]">
                <span className="text-white/55">Bonus</span>
                <span className="font-semibold text-emerald-300">
                  +৳{bonusAmt.toLocaleString("en-IN")} (total ৳
                  {totalCredit.toLocaleString("en-IN")})
                </span>
              </div>
            )}
            <button
              type="button"
              data-testid="deposit-submit"
              onClick={submit}
              disabled={!canSubmit || submitting}
              className="relative w-full h-12 sm:h-14 rounded-xl sm:rounded-2xl text-white font-display font-extrabold tracking-wide text-[15px] sm:text-base active:scale-[0.99] transition disabled:opacity-60 disabled:cursor-not-allowed overflow-hidden"
              style={{
                background:
                  "linear-gradient(180deg,#21c46e 0%,#19a55c 55%,#12904e 100%)",
                boxShadow:
                  "0 10px 28px -10px rgba(25,165,92,0.65), inset 0 1px 0 rgba(255,255,255,0.25)",
              }}
            >
              <span className="relative z-10">
                {submitting
                  ? "Processing…"
                  : amt >= minAmt
                  ? `Continue · Deposit ৳${amt.toLocaleString("en-IN")}`
                  : "Continue"}
              </span>
            </button>
          </div>
        </div>
      </div>
    </ProfileShell>
  );
}

/* ---------- end of file ---------- */


function CryptoRingIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-amber-300">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v10M9 9.5l3-2.5 3 2.5M9 14.5l3 2.5 3-2.5" />
    </svg>
  );
}
